<template>
    <b-card style="border:none;" v-loading="!isLoaded" :element-loading-text="$t('emissionLibraryLoading')">
        <b-container fluid v-if="!editMode">
            <b-row style="border-bottom:1px solid #ddd;margin-bottom:2em;justify-content:space-between;">
                <b-col cols="8" style="float:left;text-align:left;">
                    <h3>{{ this.$t('emissionLibraryManagement') }}</h3>
                </b-col>
                <b-col cols="4" style="float:right;text-align:right;">
                    <b-button key="addEmissionLibrary" style="background-color:#41B884;" lg="2" class="pb-2" @click="openInsertItemModal()"><img width="20px" height="20px" src="../../assets/icon/addIcon.svg"/> {{ $t('addEmissionLibrary') }}</b-button>
                </b-col>
            </b-row>

            <b-table 
                hover 
                responsive
                :items="data" 
                :fields="dataFields"
            >   
                <template v-slot:cell(edit)="data">
                    <div
                        @click="openEditItemModal(data.item)"
                        style="color: #8888; cursor: pointer"
                    >
                        <i class="fas fa-edit fa-lg"></i>
                    </div>
                </template>
            </b-table>
        </b-container>
        <b-container fluid v-else>
            <b-row style="border-bottom:1px solid #ddd;justify-content:space-between;">
                <b-col cols="8" style="float:left;text-align:left;">
                    <h3>{{ this.$t('editEmissionLibrary') }}</h3>
                    <h5 style="color:#808080">  {{ editedItem.name }}</h5>
                </b-col>
                <b-col cols="4" style="float:right;text-align:right;">
                    <b-button key="returnToEmissionLibrary" variant="warning" lg="2" class="pb-2" @click="returnToEmissionLibrary()" :disabled="inProgress">{{ $t('back') }}</b-button>
                    <b-button key="saveEmissionLibrary" variant="primary" lg="2" class="pb-2" @click="saveEmissionLibrary()" :disabled="inProgress || partialContentSaved"> {{ $t('save') }}</b-button>
                    <b-button v-if="editAction=='edit'" key="deleteEmissionLibrary" variant="danger" lg="2" class="pb-2" @click="deleteEmissionLibrary()" :disabled="inProgress"> {{ $t('delete') }}</b-button>
                </b-col>
            </b-row>
            <b-row style="border-bottom: 1px solid #ddd;margin-bottom:2em;">
                <b-col cols="3" class="mb-2">
                    <b-form-group
                        :label="$t('emissionLibraryName')"
                        label-for="library-name-input"
                        label-class="font-weight-bold mt-3 text-left"
                    >
                        <div>
                            <b-form-input id="library-name-input" v-model="editedItem.name" :placeholder="$t('name')"></b-form-input>
                        </div>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row style="border-bottom:1px solid #ddd;margin-bottom:2em;text-align:left;" align-v="center">
                <b-col cols="3">
                    <b-form-group
                        :label="$t('sourceRelations')"
                        label-for="source-relations"
                        label-class="font-weight-bold text-left"
                    >
                        <treeselect 
                            id="source-relations"
                            v-model="easyAdd.tags"
                            :multiple="true"
                            :clearable="true"
                            :searchable="true"
                            :max-height="500"
                            :limit="3"
                            :rtl="false"
                            :options="tagMap"
                            :disabled="false"
                            :placeholder="this.$t('selectTags')"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="2">
                    <b-form-checkbox v-model="easyAdd.generateForAllSectors">
                        <span class="formTitle">{{ $t('allSectors') }} </span>
                    </b-form-checkbox>
                </b-col>
                <b-col cols="2">
                    <b-form-checkbox v-model="easyAdd.generateForAllCountries">
                        <span class="formTitle">{{ $t('allCountries') }}</span>
                    </b-form-checkbox>
                </b-col>
                <b-col cols="2">
                    <b-form-group
                        :label="$t('country')"
                        label-for="country-select"
                        label-class="font-weight-bold text-left"
                    >
                        <b-form-select id="country-select" v-model="easyAdd.country" :disabled="easyAdd.generateForAllCountries">
                            <option :value="null" key="empty-country-1">{{ $t('Any') }}</option>
                            <option 
                                v-for="element in countryMap" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}`">{{ $t(element.name) }}</option>
                        </b-form-select>
                    </b-form-group>
                </b-col>
                <b-col cols="3" class="text-right">
                    <b-button key="autoGenerateRows" style="background-color:#41B884;" lg="2" class="pb-2" @click="autoGenerateRows()" :disabled="inProgress"><i class="fas fa-plus-circle"></i> {{ $t('autoGenerateRows') }}</b-button>
                </b-col>
            </b-row>

            <b-row style="border-bottom:1px solid #ddd;margin-bottom:2em;" align-v="center">
                <b-col cols="2" style="float:left" class="mb-3">
                    <b-form-checkbox v-model="errorFilter">
                        <span class="formTitle">{{ $t('showErrorsOnly') }}</span>
                    </b-form-checkbox>
                </b-col>
                <b-col cols="2" class="mb-3">
                    <b-button key="clearAllFilters" style="background-color:#41B884;" lg="2" class="pb-2" @click="clearAllFilters()" :disabled="inProgress"><i class="fas fa-plus"></i> {{ $t('clearAllFilters') }}</b-button>
                </b-col>
                <b-col cols="2" class="mb-3">
                    <b-button key="addNewContent" style="background-color:#41B884;" lg="2" class="pb-2" @click="addNewContent()" :disabled="inProgress"><i class="fas fa-plus"></i> {{ $t('addNewContent') }}</b-button>
                </b-col>
                <b-col v-if="editAction=='edit'" cols="6" class="mb-3 border">
                    <el-select
                        v-model="contentRetriveFilters.scopeGroupId" 
                        filterable
                        @change="updateData(contentRetriveFilters, {'sourceTypeGroupId':null,'sourceTypeId':null})">
                            <el-option 
                                v-for="element in scopeGroupMap" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-filterContentRetrive`"
                                :label="$t(element.name)"></el-option>
                    </el-select>
                    <el-select
                        v-model="contentRetriveFilters.sourceTypeGroupId" 
                        filterable
                        @change="updateData(contentRetriveFilters, {'sourceTypeId':null})">
                            <el-option 
                                v-for="element in setFilterOptionsByParentSingle(contentRetriveFilters, sourceTypeGroupMap, ['scopeGroupId'])" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-filterContentRetrive-${element.scopeGroupId}`"
                                :label="$t(element.name)"></el-option>
                    </el-select>
                    <el-select
                        v-model="contentRetriveFilters.sourceTypeId" 
                        filterable>
                            <el-option 
                                v-for="element in setFilterOptionsByParentSingle(contentRetriveFilters, sourceTypeMap, ['scopeGroupId','sourceTypeGroupId'])" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-filterContentRetrive-${element.scopeGroupId}-${element.sourceTypeGroupId}`"
                                :label="$t(element.name)"></el-option>
                    </el-select>
                    <b-button key="getEmissionLibraryContentByFilter" style="background-color:#41B884;" lg="2" class="pb-2" @click="getEmissionLibraryContentByFilter()" :disabled="inProgress"><i class="fas fa-search"></i> {{ $t('getContent') }}</b-button>
                </b-col>


            </b-row>

            <b-table 
                hover 
                responsive
                :items="getFilteredData(editedItem.content)" 
                :fields="contentFields"
                :tbody-tr-class="rowClass"
            >   
                <template v-slot:head(scopeGroupId)="head">
                    <div>
                        <el-popover
                            placement="bottom"
                            trigger="click">
                            <el-select
                                v-model="filters.scopeGroupId"
                                multiple 
                                filterable
                                collapse-tags
                                @change="updateData(filters, {'sourceTypeGroupId':[],'sourceTypeId':[]})">
                                    <el-option 
                                        v-for="element in scopeGroupMap" 
                                        :value="element.id" 
                                        :key="`${element.id}-${element.name}-filter`"
                                        :label="$t(element.name)"></el-option>
                            </el-select>
                            <span slot="reference">{{ head.label }}</span>
                        </el-popover>
                    </div>
                </template>
                <template v-slot:head(sourceTypeGroupId)="head">
                    <div>
                        <template v-if="filters.scopeGroupId.length == 0">
                            <span>{{ head.label }}</span>
                        </template>
                        <template v-else>
                            <el-popover
                                placement="bottom"
                                trigger="click">
                                <el-select
                                    v-model="filters.sourceTypeGroupId"
                                    multiple 
                                    filterable
                                    collapse-tags
                                    @change="updateData(filters, {'sourceTypeId':[]})">
                                        <el-option 
                                            v-for="element in setFilterOptionsByParent(filters, sourceTypeGroupMap, ['scopeGroupId'])" 
                                            :value="element.id" 
                                            :key="`${element.id}-${element.name}-filter-${element.scopeGroupId}`"
                                            :label="$t(element.name)"></el-option>
                                </el-select>
                                <span slot="reference">{{ head.label }}</span>
                            </el-popover>
                        </template>
                    </div>
                </template>
                <template v-slot:head(sourceTypeId)="head">
                    <div>
                        <template v-if="filters.sourceTypeGroupId.length == 0">
                            <span>{{ head.label }}</span>
                        </template>
                        <template v-else>
                            <el-popover
                                placement="bottom"
                                trigger="click">
                                <el-select
                                    v-model="filters.sourceTypeId"
                                    multiple 
                                    filterable
                                    collapse-tags>
                                        <el-option 
                                            v-for="element in setFilterOptionsByParent(filters, sourceTypeMap, ['scopeGroupId','sourceTypeGroupId'])" 
                                            :value="element.id" 
                                            :key="`${element.id}-${element.name}-filter-${element.scopeGroupId}-${element.sourceTypeGroupId}`"
                                            :label="$t(element.name)"></el-option>
                                </el-select>
                                <span slot="reference">{{ head.label }}</span>
                            </el-popover>
                        </template>
                    </div>
                </template>
                <template v-slot:head(countryId)="head">
                    <div>
                        <el-popover
                            placement="bottom"
                            trigger="click">
                            <el-select
                                v-model="filters.countryId"
                                multiple 
                                filterable
                                collapse-tags
                                @change="updateData(filters, {'region':[]})">
                                    <el-option 
                                        v-for="element in countryMap" 
                                        :value="element.id" 
                                        :key="`${element.id}-${element.name}-filter`"
                                        :label="$t(element.name)"></el-option>
                            </el-select>
                            <span slot="reference">{{ head.label }}</span>
                        </el-popover>
                    </div>
                </template>
                <template v-slot:head(regionId)="head">
                    <div>
                        <el-popover
                            placement="bottom"
                            trigger="click">
                            <el-select
                                v-model="filters.regionId"
                                multiple 
                                filterable
                                collapse-tags>
                                    <el-option 
                                        v-for="element in regionMap" 
                                        :value="element.id" 
                                        :key="`${element.id}-${element.name}-filter-${element.countryId}`"
                                        :label="$t(element.name)"></el-option>
                            </el-select>
                            <span slot="reference">{{ head.label }}</span>
                        </el-popover>
                    </div>
                </template>
                <template v-slot:head(sectorId)="head">
                    <div>
                        <el-popover
                            placement="bottom"
                            trigger="click">
                            <el-select
                                v-model="filters.sectorId"
                                multiple 
                                filterable
                                collapse-tags>
                                    <el-option 
                                        v-for="element in sectorMap" 
                                        :value="element.id" 
                                        :key="`${element.id}-${element.name}-filter`"
                                        :label="$t(element.name)"></el-option>
                            </el-select>
                            <span slot="reference">{{ head.label }}</span>
                        </el-popover>
                    </div>
                </template>
                <template v-slot:head(unitId)="head">
                    <div>
                        <template>
                            <el-popover
                                placement="bottom"
                                trigger="click">
                                <el-select
                                    v-model="filters.unitId"
                                    multiple 
                                    filterable
                                    collapse-tags>
                                        <el-option 
                                            v-for="element in unitMap" 
                                            :value="element.id" 
                                            :key="`${element.id}-${element.name}-filter-${element.sourceTypeId}`"
                                            :label="$t(element.name)"></el-option>
                                </el-select>
                                <span slot="reference">{{ head.label }}</span>
                            </el-popover>
                        </template>
                    </div>
                </template>
                <template v-slot:head(edit)="head" v-if="editAction == 'edit'">
                    <span @click="getEmissionLibraryContentByFilter()"><i class="fas fa-redo"></i></span>
                </template>

                <template v-slot:cell(scopeGroupId)="data">
                    <b-form-select 
                        v-model="data.item['scopeGroupId']"
                        @change="updateData(data.item,{'sourceTypeGroupId':null,'sourceTypeId':null})">
                            <option 
                                v-for="element in scopeGroupMap" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-nid-${data.item.id}`">{{ $t(element.name) }}</option>
                    </b-form-select>
                </template>
                <template v-slot:cell(sourceTypeGroupId)="data">
                    <b-form-select 
                        v-if="data.item['scopeGroupId']"
                        v-model="data.item['sourceTypeGroupId']"
                        @change="updateData(data.item,{'sourceTypeId':null})" >
                            <option 
                                v-for="element in filterByParent(sourceTypeGroupMap, [{value:data.item.scopeGroupId, key:'scopeGroupId'}])" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-nid-${data.item.id}`">{{ $t(element.name) }}</option>
                    </b-form-select>
                </template>
                <template v-slot:cell(sourceTypeId)="data">
                    <b-form-select 
                        v-if="data.item['sourceTypeGroupId']"
                        v-model="data.item['sourceTypeId']"
                        @change="forceUpdate" >
                            <option 
                                v-for="element in filterByParent(sourceTypeMap, [{value:data.item.sourceTypeGroupId, key:'sourceTypeGroupId'},{value:data.item.scopeGroupId, key:'scopeGroupId'}])" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-nid-${data.item.id}`">{{ $t(element.name) }}</option>
                    </b-form-select>
                </template>
                <template v-slot:cell(countryId)="data">
                    <b-form-select 
                        v-model="data.item['countryId']"
                        @change="updateData(data.item,{'regionId':null})" >
                            <option :value="null" key="empty-country-2">-</option>
                            <option 
                                v-for="element in countryMap" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-nid-${data.item.id}`">{{ $t(element.name) }}</option>
                    </b-form-select>
                </template>
                <template v-slot:cell(regionId)="data">
                    <b-form-select 
                        v-if="data.item['countryId']"
                        v-model="data.item['regionId']"
                        @change="forceUpdate" >
                            <option :value="null" key="empty-region">-</option>
                            <option 
                                v-for="element in filterByParent(regionMap, [{value:data.item.countryId, key:'countryId'}])" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-nid-${data.item.id}`">{{ $t(element.name) }}</option>
                    </b-form-select>
                </template>
                <template v-slot:cell(sectorId)="data">
                    <b-form-select 
                        v-model="data.item['sectorId']"
                        @change="forceUpdate" >
                            <option :value="null" key="empty-sector">-</option>
                            <option 
                                v-for="element in sectorMap" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-nid-${data.item.id}`">{{ $t(element.name) }}</option>
                    </b-form-select>
                </template>
                <template v-slot:cell(unitId)="data">
                    <b-form-select 
                        v-if="data.item['sourceTypeId']"
                        v-model="data.item['unitId']"
                        @change="forceUpdate" >
                            <option 
                                v-for="element in getUnitMap(data.item)" 
                                :value="element.id" 
                                :key="`${element.id}-${element.name}-nid-${data.item.id}`">{{ $t(element.name) }}</option>
                    </b-form-select>
                </template>
                <template v-slot:cell(CH4)="data">
                    <b-form-input v-model="data.item['CH4']" @change="forceUpdate"/>
                </template>
                <template v-slot:cell(N2O)="data">
                    <b-form-input v-model="data.item['N2O']" @change="forceUpdate"/>
                </template>
                <template v-slot:cell(CO2)="data">
                    <b-form-input v-model="data.item['CO2']" @change="forceUpdate"/>
                </template>
                <template v-slot:cell(CO2e)="data">
                    <b-form-input v-model="data.item['CO2e']" @change="forceUpdate"/>
                </template>
                <template v-slot:cell(edit)="data">
                    <div class="tableAction">
                        <div
                            @click="duplicateContent(data.item)"
                            style="color: #8888; cursor: pointer; float: left;"
                            v-b-tooltip.hover :title="$t('duplicateItem')"
                        >
                            <i class="fas fa-clone fa-lg"></i>
                        </div>
                        <div
                            @click="contentSplitCountryToRegions(data.item)"
                            style="color: #8888; cursor: pointer; float:center; color:#4F8EC1;"
                            v-if="showSplitRegionsButton(data.item)"
                            v-b-tooltip.hover :title="$t('splitCountryToRegions')"
                        >
                            <i class="fas fa-globe fa-lg"></i>
                        </div>
                        <div
                            @click="deleteContent(data.item)"
                            style="color: #8888; cursor: pointer; float: right; color:#d04d4d;"
                            v-b-tooltip.hover :title="$t('delete')"
                        >
                            <i class="fas fa-trash-alt fa-lg"></i>
                        </div>
                    </div>
                </template>
            </b-table>
        </b-container>
    </b-card>
</template>

<script>
import _ from 'lodash';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import uuidV4 from 'uuid/v4';
//import moment from 'moment';

export default {
    name: 'EmissionLibrary',
    components: {
        Treeselect
    },
    data() {
        return {
            dataFields: [
                {
                    label: this.$t('name'),
                    key: 'name',
                    thClass: 'table-point-th',
                    sortable: true,
                    stickyColumn: true
                },
                {
                    label: this.$t('edit'),
                    key: 'edit',
                    thClass: 'table-point-th',
                    stickyColumn: true
                }
            ],
            contentFields: [
                {
                    label: this.$t('scopeGroup'),
                    key: 'scopeGroupId',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-tag-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('sourceTypeGroup'),
                    key: 'sourceTypeGroupId',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-tag-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('sourceType'),
                    key: 'sourceTypeId',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-tag-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('country'),
                    key: 'countryId',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-location-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('region'),
                    key: 'regionId',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-location-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('sector'),
                    key: 'sectorId',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-location-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('unit'),
                    key: 'unitId',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-unit-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('CH4'),
                    key: 'CH4',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-emission-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('N2O'),
                    key: 'N2O',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-emission-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('CO2'),
                    key: 'CO2',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-emission-group',
                    stickyColumn: true
                },
                {
                    label: this.$t('CO2e'),
                    key: 'CO2e',
                    sortable: true,
                    thClass: 'table-point-th',
                    tdClass: 'table-emission-group',
                    stickyColumn: true
                },
                {
                    label: '',
                    key: 'edit',
                    thClass: 'table-point-th',
                    tdClass: 'table-emission-group',
                    stickyColumn: true
                }
            ],
            data: [],
            editedItem: {},
            editMode: false,
            isLoaded: false,
            easyAdd: {
                tags:[],
                generateForAllSectors: false,
                generateForAllCountries: false,
                country: null
            },
            errorFilter: false,
            filters:{
                scopeGroupId:[],
                sourceTypeGroupId:[],
                sourceTypeId:[],
                countryId:[],
                regionId:[],
                sectorId:[],
                unitId:[]
            },
            editAction: null,
            inProgress: false,
            contentRetriveFilters:{
                libraryId: null,
                sourceTypeId: null,
                sourceTypeGroupId: null,
                scopeGroupId: null
            },
            tableContent: {},
            partialContentSaved: true
        };
    },
    methods: {
        forceUpdate() {
            this.$forceUpdate();
        },
        saveEmissionLibrary(){
            this.inProgress = true;
            switch (this.editAction){
                case 'edit':
                    return this.axios.put('/emissionLibrary', {emissionLibrary: this.editedItem, tableContent: this.tableContent})
                        .then(()=>{
                            /*
                            this.editedItem.content = _.map(data.result, o=>{
                                return {
                                    id: uuidV4(),
                                    countryId: _.get(o, 'country_id'),
                                    regionId: _.get(o, 'region_id'),
                                    scopeGroupId: _.get(o, 'scope_group_id'),
                                    sectorId: _.get(o, 'sector_id'),
                                    sourceTypeGroupId: _.get(o, 'source_type_group_id'),
                                    sourceTypeId: _.get(o, 'source_type_id'),
                                    unitId: _.get(o, 'unit_id'),
                                    CH4: _.get(o, 'CH4'),
                                    CO2: _.get(o, 'CO2'),
                                    CO2e: _.get(o, 'CO2e'),
                                    N2O: _.get(o, 'N2O')
                                }
                            });
                            let currentEmissionLibraryId = _.findIndex(this.data, o=>o.id==this.editedItem.id);
                            this.data[currentEmissionLibraryId] = _.cloneDeep(this.editedItem);
                            this.returnToEmissionLibrary();
                            */
                            this.partialContentSaved=true;
                            this.inProgress = false;
                            //this.retriveEmissionLibraries();
                            
                        });
                case 'insert':
                    return this.axios.post('/emissionLibrary', this.editedItem)
                        .then(()=>{
                            this.inProgress = false;
                            this.retriveEmissionLibraries();
                        });
                case 'delete':
                    return this.deleteEmissionLibrary();
            }
        },
        deleteEmissionLibrary(){
            return this.axios.delete(`/emissionLibrary/${this.editedItem.id}`)
                .then(()=>{
                    this.data = _.filter(this.data, o=>o.id!=this.editedItem.id);
                    this.returnToEmissionLibrary();
                }).catch(() => {
                    this.showErrorNotification('deletion-failed');
                });
        },
        clearAllFilters() {
            this.filters = {
                scopeGroupId:[],
                sourceTypeGroupId:[],
                sourceTypeId:[],
                countryId:[],
                regionId:[],
                sectorId:[],
                unitId:[]
            }
        },
        clearUserInputs(){
            this.editAction = null
            this.easyAdd = {
                    tags:[],
                    generateForAllSectors: false,
                    generateForAllCountries: false,
                    country: null
                };
            this.errorFilter = false;
            this.clearAllFilters()
        },
        setFilterOptionsByParent(filters,data,filterKeys){
            return _.filter(data, o=>{
                let filterFlag = true;
                _.each(filterKeys, filterKey=>{
                    let selectedFilter = _.map(_.get(filters, filterKey), _.toString)
                    if(!_.isEmpty(selectedFilter)){
                        if(!_.includes(selectedFilter,_.toString(o[filterKey]))){
                            filterFlag=false;
                        }
                    }
                });
                return filterFlag;
            });
        },
        setFilterOptionsByParentSingle(filters,data,filterKeys){
            return _.filter(data, o=>{
                let filterFlag = true;
                _.each(filterKeys, filterKey=>{
                    let selectedFilter = _.get(filters, filterKey)
                    if(!_.isNull(selectedFilter)){
                        if(_.toString(selectedFilter) != _.toString(o[filterKey])){
                            filterFlag=false;
                        }
                    }
                });
                return filterFlag;
            });
        },
        filterByParent(itemMap, filters){
            return _.uniqBy(
                _.filter(itemMap, o=>{
                    let filterFlag = true;
                    _.each(filters, filter=>{
                        if(_.toNumber(o[filter.key])!=_.toNumber(filter.value)){
                            filterFlag = false
                        }
                    })
                    return filterFlag;
                }),
                'name'
            );
        },
        getFilteredData(data){
            return _.filter(data, o=>{
                let filterFlag = true;
                _.each(this.filters, (filterValues,filterKey)=>{
                    if(!_.isEmpty(filterValues)){
                        if(!_.includes(filterValues,o[filterKey])){
                            filterFlag=false;
                        }
                    }   
                });
                if(this.errorFilter){
                    if(!_.includes(['warning','danger'], _.get(o,'_rowVariant'))){
                        filterFlag=false;
                    }
                }
                return filterFlag
            })
        },
        updateData(data,updatedObjs){
            _.each(updatedObjs, (value,key)=>{
                data[key]=value
            });
            this.forceUpdate();
        },
        getUnitMap(sourceTypeId){
            let sourceType = _.find(this.sourceTypeMap, o=>_.toNumber(o.id)==_.toNumber(sourceTypeId.sourceTypeId));
            return sourceType.units;
        },
        duplicateContent(item){
            let newItem = _.cloneDeep(item);
            newItem.id = null;
            this.editedItem.content.push(newItem);
        },
        contentSplitCountryToRegions(item){
            let countryId = item.countryId;
            let regions = _.filter(this.regionMap, o=>_.toNumber(o['countryId'])==_.toNumber(countryId));
            _.each(regions, region=>{
                let newItem = _.cloneDeep(item);
                newItem.id = null;
                newItem.regionId = region.id;
                this.editedItem.content.push(newItem);
            });
            this.deleteContent(item);
        },
        autoGenerateRows(){
            _.each(this.easyAdd.tags, tagString=>{

                let keys = _.split(tagString, '-');
                let relatedTags = [];
                if(_.size(keys) == 3){
                    relatedTags.push({
                        scopeGroupId: _.toNumber(keys[0]),
                        sourceTypeGroupId: _.toNumber(keys[1]),
                        id: _.toNumber(keys[2])
                    })
                }
                else if(_.size(keys) == 2){
                    relatedTags = _.filter(this.sourceTypeMap, o=>(_.toNumber(o.scopeGroupId) == _.toNumber(keys[0]) && _.toNumber(o.sourceTypeGroupId) == _.toNumber(keys[1])))
                }
                else if(_.size(keys) == 1){
                    relatedTags = _.filter(this.sourceTypeMap, o=>_.toNumber(o.scopeGroupId) == _.toNumber(keys[0]))
                }
                _.each(relatedTags, relatedTagGroup=>{
                    if(this.easyAdd.generateForAllCountries){
                        _.each(this.countryMap, country=>{
                            if(this.easyAdd.generateForAllSectors){
                                _.each(this.sectorMap, sector=>{
                                    this.editedItem.content.push({
                                        id: uuidV4(),
                                        scopeGroupId: _.toNumber(relatedTagGroup.scopeGroupId),
                                        sourceTypeGroupId: relatedTagGroup.sourceTypeGroupId,
                                        sourceTypeId: relatedTagGroup.id,
                                        countryId: country.id,
                                        regionId: null,
                                        sectorId: sector.id,
                                        unitId: null,
                                        CH4: null,
                                        N2O: null,
                                        CO2: null,
                                        CO2e: null
                                    });
                                });
                            }
                            else{
                                this.editedItem.content.push({
                                    id: uuidV4(),
                                    scopeGroupId: _.toNumber(relatedTagGroup.scopeGroupId),
                                    sourceTypeGroupId: relatedTagGroup.sourceTypeGroupId,
                                    sourceTypeId: relatedTagGroup.id,
                                    countryId: country.id,
                                    regionId: null,
                                    sectorId: null,
                                    unitId: null,
                                    CH4: null,
                                    N2O: null,
                                    CO2: null,
                                    CO2e: null
                                });
                            }
                        });
                    }
                    else{
                        if(this.easyAdd.generateForAllSectors){
                            _.each(this.sectorMap, sector=>{
                                this.editedItem.content.push({
                                    id: uuidV4(),
                                    scopeGroupId: _.toNumber(relatedTagGroup.scopeGroupId),
                                    sourceTypeGroupId: relatedTagGroup.sourceTypeGroupId,
                                    sourceTypeId: relatedTagGroup.id,
                                    countryId: this.easyAdd.country,
                                    regionId: null,
                                    sectorId: sector.id,
                                    unitId: null,
                                    CH4: null,
                                    N2O: null,
                                    CO2: null,
                                    CO2e: null
                                });
                            });
                        }
                        else{
                            this.editedItem.content.push({
                                id: uuidV4(),
                                scopeGroupId: _.toNumber(relatedTagGroup.scopeGroupId),
                                sourceTypeGroupId: relatedTagGroup.sourceTypeGroupId,
                                sourceTypeId: relatedTagGroup.id,
                                countryId: this.easyAdd.country,
                                regionId: null,
                                sectorId: null,
                                unitId: null,
                                CH4: null,
                                N2O: null,
                                CO2: null,
                                CO2e: null
                            });
                        }
                    }

                });
            });
        },
        addNewContent(){
            this.editedItem.content.push({
                id: uuidV4(),
                scopeGroupId:null,
                sourceTypeGroupId:null,
                sourceTypeId:null,
                countryId:null,
                regionId:null,
                sectorId:null,
                unitId:null,
                CH4:null,
                N2O:null,
                CO2:null,
                CO2e:null
            });
        },
        deleteContent(item){
            this.editedItem.content = _.filter(this.editedItem.content, o=> o!=item);
        },
        rowClass(item,type) {
            if(!item ||type !== 'row') return
            else{
                if(
                    _.isNull(item.scopeGroupId)
                    || _.isNull(item.sourceTypeGroupId)
                    || _.isNull(item.sourceTypeId)
                    || _.isNull(item.unitId)
                    || (
                            (_.isNull(item.CH4) || item.CH4 == '')
                            && (_.isNull(item.N2O) || item.N2O == '')
                            && (_.isNull(item.CO2) || item.CO2 == '')
                            && (_.isNull(item.CO2e) || item.CO2e == '')
                        )
                ){
                    item._rowVariant = 'warning';
                }
                else if(_.size(_.filter(this.editedItem.content, o => 
                    o.scopeGroupId == item.scopeGroupId
                    && o.sourceTypeGroupId == item.sourceTypeGroupId
                    && o.sourceTypeId == item.sourceTypeId
                    && o.countryId == item.countryId
                    && o.regionId == item.regionId
                    && o.sectorId == item.sectorId
                    && o.unitId == item.unitId
                ))>1){
                    item._rowVariant = 'danger';
                    return 'table-danger';
                }
                else{
                    item._rowVariant = 'light';
                }
            }
        },
        showSplitRegionsButton(item){
            //let regions = _.find(this.editedItem.content, o=> o.countryId==countryId && o.regionId != null)
            //return regions == undefined;
            return (item.regionId == undefined || item.regionId == null) && item.countryId != null;
        },
        openEditItemModal(item){
            this.editedItem = {..._.cloneDeep(item), content:[]}
            this.contentRetriveFilters.libraryId = item.id;
            /*
            _.each(this.editedItem.content, content=>{
                content['scopeGroup']= this.$t(this.scopeGroupMap[content.scopeGroupId].name)
                content['sourceTypeGroup']= this.$t(this.sourceTypeGroupMap[content.sourceTypeGroupId].name)
                content['sourceType']= this.$t(this.sourceTypeMap[content.sourceTypeId].name)
                content['country']= this.$t(this.countryMap[content.countryId].name)
                content['region']= this.$t(this.regionMap[content.regionId].name)
                content['sector']= this.$t(this.sectorMap[content.sectorId].name)
                content['unit']= this.$t(this.unitMap[content.unitId].name)
            });
            */
            this.clearUserInputs()
            this.editMode = true;
            this.editAction = 'edit';
            this.partialContentSaved = true;
            this.forceUpdate();
        },
        openInsertItemModal() {
            this.editedItem = {
                name: '',
                content: []
            }
            this.tableContent = {};
            this.clearUserInputs()
            this.editAction = 'insert';
            this.partialContentSaved = true;
            this.editMode = true;
        },
        returnToEmissionLibrary(){
            if (this.editAction == 'edit' && !this.partialContentSaved) {
                this.$confirm(this.$t('thereAreChanges'), this.$t('warning'), {
                    confirmButtonText: this.$t('saveChanges'),
                    cancelButtonText: this.$t('leaveWithoutSaving'),
                    type: 'warning',
                    center: true
                })
                    .then(() => this.saveEmissionLibrary().then(()=> this.retriveEmissionLibraries()))
                    .catch(() => this.returnToParent());
            } else {
                this.returnToParent();
            }
        },
        returnToParent(){
            this.tableContent = {};
            this.editedItem = {};
            this.clearUserInputs();
            this.editMode = false;
        },
        preprocess(rawData){
            this.data = rawData;
        },
        contentProcess(rawData){
             this.editedItem.content = rawData;
        },
        getEmissionLibraryContent(){
            this.$Progress.start();
            this.isLoaded = false;
            return this.axios.post('/emissionLibraryContent', this.contentRetriveFilters)
                .then(({data})=> this.contentProcess(_.cloneDeep(data.result)))
                .then(()=>{
                    this.tableContent = _.clone(this.contentRetriveFilters);
                    this.partialContentSaved = true;
                    this.$Progress.finish();
                    this.isLoaded = true;
                });
        },
        getEmissionLibraryContentByFilter(){
            if (this.editAction == 'edit' && !this.partialContentSaved) {
                this.$confirm(this.$t('thereAreChanges'), this.$t('warning'), {
                    confirmButtonText: this.$t('saveChanges'),
                    cancelButtonText: this.$t('contiuneWithoutSaving'),
                    type: 'warning',
                    center: true
                })
                    .then(() => this.saveEmissionLibrary().then(()=> this.getEmissionLibraryContent()))
                    .catch(() => this.getEmissionLibraryContent());
            } else {
                this.getEmissionLibraryContent();
            }
        },
        retriveEmissionLibraries(){
            this.$Progress.start();
            this.isLoaded = false;
            return this.axios.get('/emissionLibrary')
                .then(({data}) => this.preprocess(data))
                .then(()=>{
                    this.$Progress.finish();
                    this.returnToEmissionLibrary();
                    this.isLoaded = true;
                });
        },
        init() {
            this.$Progress.start();
            return this.axios.get('/emissionLibrary').then(({data}) => 
                this.preprocess(data)).then(() => {
                    this.$Progress.finish();
                    this.returnToEmissionLibrary();
                    this.isLoaded = true;
                })
        }
    },
    watch:{
        editedItem:{
            handler: function(newItem,oldItem){
                if(!_.isEmpty(oldItem)){
                    this.partialContentSaved = false;
                }
                else if(!_.isEmpty(newItem)){
                    this.partialContentSaved = true;
                }
            },
            deep: true
        },
        contentRetriveFilters:{
            handler: function(){
                this.forceUpdate();
            },
            deep: true
        }
    },
    created(){
        this.init()
    },
    beforeRouteLeave(to, from, next) {
        if (this.editAction == 'edit' && !this.partialContentSaved) {
            this.$confirm(this.$t('thereAreChanges'), this.$t('warning'), {
                confirmButtonText: this.$t('saveChanges'),
                cancelButtonText: this.$t('leaveWithoutSaving'),
                type: 'warning',
                center: true
            })
                .then(() => this.saveEmissionLibrary())
                .catch(() => next());
        } else {
            next();
        }
    },
        
};
</script>

<style lang="scss" scoped>
    .formTitle {
        text-align: left;
        font-weight:bold;
        color: #022b49;
    }
    .parentTab:hover {
        cursor: pointer;
        text-decoration: underline;
    }
    .tableAction {
        display: flex;
        justify-content: space-between;
    }
    /deep/ .table-tag-group {
        width: 3em;
    }
    /deep/ .table-location-group {
        width: 2em;
    }
    /deep/ .table-unit-group {
        width: 1.5em;
    }
    /deep/ .table-emission-group {
        width: 5em;
    }

</style>

<i18n src="../../i18n/common.json"></i18n>

<i18n>
    {
        "tr": {
            "emissionLibraryManagement": "Emisyon Kütüphanesi Yönetimi",
            "addEmissionLibrary": "Emisyon Kütüphanesi Ekle",
            "editEmissionLibrary": "Emisyon Kütüphanesi Düzenle",
            "emissionLibraryName": "Emisyon Kütüphanesi Adı",
            "sourceRelations": "Kaynak Etiket Bağlantıları",
            "allSectors": "Bütün Sektörler",
            "allCountries": "Bütün Ülkeler",
            "autoGenerateRows": "Verileri Oluştur",
            "showErrorsOnly": "Hatalı Satırları Göster",
            "clearAllFilters": "Filtreleri kaldır",
            "addNewContent": "Yeni Satır Ekle",
            "scopeGroup": "Scope Group",
            "sourceTypeGroup": "Source Type Group",
            "sourceType": "Source Type",
            "unit": "Birim"
        },
        "en": {
            "emissionLibraryManagement": "Emission Library Management",
            "addEmissionLibrary": "Add Emission Library",
            "editEmissionLibrary": "Edit Emission Library",
            "sourceRelations": "Source Relations",
            "allSectors": "All Sectors",
            "allCountries": "All Countries",
            "autoGenerateRows": "Auto Generate Rows",
            "showErrorsOnly": "Show Errors Only",
            "clearAllFilters": "Clear All Filters",
            "addNewContent": "Add New Content",
            "scopeGroup": "Scope Group",
            "sourceTypeGroup": "Source Type Group",
            "sourceType": "Source Type",
            "unit": "Unit"
        }
    }
</i18n>



// WEBPACK FOOTER //
// src/views/Setting/EmissionLibrary.vue